import React, { useEffect, useState } from "react";
import Shell from "@/components/layout/Shell";
import { PageHeader, formatCOP, EmptyState, GhostButton } from "@/components/common";
import { api, API } from "@/lib/api";
import { BarChart3, TrendingUp, RefreshCw, Users, Download, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Legend } from "recharts";

const monthLabel = (mk) => {
  const [y, m] = mk.split("-");
  const names = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${names[parseInt(m)-1]} ${y.slice(2)}`;
};

const Card = ({ title, subtitle, children, icon: Icon, testid }) => (
  <div data-testid={testid} className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h3>
        {subtitle && <p className="text-xs text-[#9CA3AF]">{subtitle}</p>}
      </div>
      {Icon && <Icon className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/>}
    </div>
    {children}
  </div>
);

export default function Reportes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports").then(r => setData(r.data)).finally(()=>setLoading(false));
  }, []);

  if (loading || !data) return <Shell><div className="text-[#9CA3AF]">Cargando…</div></Shell>;

  const downloadFile = async (kind, format) => {
    try {
      const resp = await api.get(`/reports/export/${kind}.${format}`, { responseType: "blob" });
      const mime = format === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}_${new Date().toISOString().slice(0,10)}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };
  const downloadCsv = (kind) => downloadFile(kind, "csv");
  const downloadPdf = (kind) => downloadFile(kind, "pdf");

  const revenueData = data.revenue_12_months.map(m => ({ ...m, label: monthLabel(m.month) }));
  const appsData = data.appointments_by_month.map(m => ({ ...m, label: monthLabel(m.month) }));
  const hasData = data.revenue_12_months.some(m => m.revenue > 0) || data.client_ranking.length > 0;

  return (
    <Shell>
      <PageHeader
        title="Reportes"
        subtitle="Análisis del desempeño de tu negocio"
        action={
          <div className="flex gap-2 flex-wrap">
            <div className="inline-flex items-center rounded-xl border border-[#262A33] bg-[#15171E] overflow-hidden">
              <span className="px-3 py-2 text-[11px] font-medium text-[#9CA3AF] border-r border-[#262A33]">Ingresos</span>
              <button data-testid="export-revenue-csv" onClick={()=>downloadCsv("revenue")} className="px-3 py-2 text-xs text-[#F5F5F7] hover:bg-[#1A1D24] transition-colors flex items-center gap-1"><Download className="w-3.5 h-3.5"/>CSV</button>
              <button data-testid="export-revenue-pdf" onClick={()=>downloadPdf("revenue")} className="px-3 py-2 text-xs text-[#F97316] hover:bg-[#F97316]/10 transition-colors flex items-center gap-1 border-l border-[#262A33]"><FileText className="w-3.5 h-3.5"/>PDF</button>
            </div>
            <div className="inline-flex items-center rounded-xl border border-[#262A33] bg-[#15171E] overflow-hidden">
              <span className="px-3 py-2 text-[11px] font-medium text-[#9CA3AF] border-r border-[#262A33]">Clientes</span>
              <button data-testid="export-clients-csv" onClick={()=>downloadCsv("clients")} className="px-3 py-2 text-xs text-[#F5F5F7] hover:bg-[#1A1D24] transition-colors flex items-center gap-1"><Download className="w-3.5 h-3.5"/>CSV</button>
              <button data-testid="export-clients-pdf" onClick={()=>downloadPdf("clients")} className="px-3 py-2 text-xs text-[#F97316] hover:bg-[#F97316]/10 transition-colors flex items-center gap-1 border-l border-[#262A33]"><FileText className="w-3.5 h-3.5"/>PDF</button>
            </div>
            <div className="inline-flex items-center rounded-xl border border-[#262A33] bg-[#15171E] overflow-hidden">
              <span className="px-3 py-2 text-[11px] font-medium text-[#9CA3AF] border-r border-[#262A33]">Facturas</span>
              <button data-testid="export-invoices-csv" onClick={()=>downloadCsv("invoices")} className="px-3 py-2 text-xs text-[#F5F5F7] hover:bg-[#1A1D24] transition-colors flex items-center gap-1"><Download className="w-3.5 h-3.5"/>CSV</button>
              <button data-testid="export-invoices-pdf" onClick={()=>downloadPdf("invoices")} className="px-3 py-2 text-xs text-[#F97316] hover:bg-[#F97316]/10 transition-colors flex items-center gap-1 border-l border-[#262A33]"><FileText className="w-3.5 h-3.5"/>PDF</button>
            </div>
          </div>
        }
      />

      {!hasData ? (
        <EmptyState icon={BarChart3} title="Aún no hay datos suficientes"
          description="A medida que agregues clientes, cobros y citas, este panel se llenará automáticamente con tus estadísticas."
        />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
              <div className="text-xs text-[#9CA3AF] mb-2">Tasa de retención</div>
              <div className="text-3xl font-bold text-emerald-400" style={{ fontFamily: "Manrope, sans-serif" }}>{data.retention_rate}%</div>
              <div className="text-[11px] text-[#6B7280] mt-1">Clientes activos vs total</div>
            </div>
            <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
              <div className="text-xs text-[#9CA3AF] mb-2">Ingresos 12 meses</div>
              <div className="text-3xl font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>
                {formatCOP(data.revenue_12_months.reduce((s,m)=>s+m.revenue,0))}
              </div>
            </div>
            <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
              <div className="text-xs text-[#9CA3AF] mb-2">Top cliente</div>
              <div className="text-xl font-bold text-[#F97316] truncate" style={{ fontFamily: "Manrope, sans-serif" }}>
                {data.client_ranking[0]?.name || "—"}
              </div>
              <div className="text-[11px] text-[#6B7280] mt-1">{data.client_ranking[0] ? formatCOP(data.client_ranking[0].total) : ""}</div>
            </div>
          </div>

          <Card title="Ingresos mensuales" subtitle="Últimos 12 meses" icon={TrendingUp} testid="report-revenue-chart">
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262A33" vertical={false}/>
                  <XAxis dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v)=>`$${(v/1000).toFixed(0)}k`}/>
                  <Tooltip contentStyle={{ background: "#15171E", border: "1px solid #262A33", borderRadius: 12 }} formatter={(v)=>[formatCOP(v),"Ingresos"]}/>
                  <Bar dataKey="revenue" fill="#F97316" radius={[8,8,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="Ranking de clientes por facturación" icon={BarChart3} testid="report-client-ranking">
              {data.client_ranking.length === 0 ? (
                <p className="text-sm text-[#9CA3AF]">Sin facturas cobradas aún.</p>
              ) : (
                <div className="space-y-2">
                  {data.client_ranking.map((c, i) => {
                    const max = data.client_ranking[0].total || 1;
                    const pct = (c.total / max) * 100;
                    return (
                      <div key={c.client_id}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-[#F5F5F7]"><span className="text-[#F97316] font-bold">#{i+1}</span> {c.name}</span>
                          <span className="text-[#9CA3AF]">{formatCOP(c.total)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[#0B0D12] overflow-hidden">
                          <div className="h-full bg-[#F97316]" style={{ width: `${pct}%` }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card title="Citas realizadas vs canceladas" subtitle="Últimos 6 meses" icon={RefreshCw} testid="report-appointments-chart">
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <LineChart data={appsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#262A33" vertical={false}/>
                    <XAxis dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false}/>
                    <Tooltip contentStyle={{ background: "#15171E", border: "1px solid #262A33", borderRadius: 12 }}/>
                    <Legend wrapperStyle={{ fontSize: 12, color: "#9CA3AF" }}/>
                    <Line type="monotone" dataKey="realizadas" stroke="#10B981" strokeWidth={2}/>
                    <Line type="monotone" dataKey="canceladas" stroke="#EF4444" strokeWidth={2}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card title="Desempeño del equipo" icon={Users} testid="report-team-performance">
            {data.team_performance.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">Sin miembros en el equipo aún.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#9CA3AF] border-b border-[#262A33]">
                      <th className="text-left font-medium py-2">Miembro</th>
                      <th className="text-left font-medium py-2">Rol</th>
                      <th className="text-right font-medium py-2">Clientes</th>
                      <th className="text-right font-medium py-2">Citas realizadas</th>
                      <th className="text-right font-medium py-2">Total citas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.team_performance.map(t => (
                      <tr key={t.member_id} className="border-b border-[#262A33] last:border-0">
                        <td className="py-3 text-[#F5F5F7]">{t.name}</td>
                        <td className="py-3 text-[#9CA3AF]">{t.role}</td>
                        <td className="py-3 text-right text-[#F5F5F7]">{t.clients}</td>
                        <td className="py-3 text-right text-emerald-400 font-medium">{t.appointments_done}</td>
                        <td className="py-3 text-right text-[#F5F5F7]">{t.appointments_total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </Shell>
  );
}
